import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink } from "lucide-react";
import FirmIcon from "./FirmIcon";
import DiscountBadge from "./DiscountBadge";

export default function OfferPopup({ firms }) {
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    if (!firms.length || sessionStorage.getItem("te_offer_seen")) return;
    const best = [...firms].filter((f) => f.discount_code).sort((a, b) => b.rating - a.rating)[0];
    if (!best) return;
    const t = setTimeout(() => {
      setOffer(best);
      sessionStorage.setItem("te_offer_seen", "1");
    }, 1600);
    return () => clearTimeout(t);
  }, [firms]);

  const dismiss = () => setOffer(null);

  return (
    <AnimatePresence>
      {offer && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-5 backdrop-blur-sm"
          data-testid="offer-popup"
          onClick={dismiss}
        >
          <motion.div
            initial={{ scale: 0.92, y: 24 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, y: 12 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm rounded-3xl border border-signal/30 bg-surface p-8 text-center shadow-[0_0_80px_rgba(0,212,255,0.3)]"
          >
            <button
              onClick={dismiss}
              data-testid="offer-close-btn"
              aria-label="Close offer"
              className="absolute right-4 top-4 text-muted transition-colors hover:text-white"
            >
              <X size={18} />
            </button>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-line bg-elevate">
              <FirmIcon name={offer.icon} size={28} logo={offer.logo} />
            </div>
            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.35em] text-signal">{offer.name}</p>
            <h2 className="mt-2 font-display text-2xl font-900 leading-tight tracking-tight text-white">
              EXCLUSIVE {offer.discount_label}<br />OFFER!
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted">
              Use code <span className="font-mono font-700 text-white">{offer.discount_code}</span> at
              checkout on {offer.name} and claim your Funded Traders Club member discount.
            </p>
            <div className="mt-5 flex justify-center">
              <DiscountBadge label={offer.discount_label} code={offer.discount_code} slug={`popup-${offer.slug}`} />
            </div>
            <a
              href={offer.website_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid="offer-get-btn"
              className="btn-beam btn-gradient mt-5 flex w-full items-center justify-center gap-2 px-5 py-3.5 font-display text-sm font-800 text-white"
            >
              Get Offer <ExternalLink size={14} />
            </a>
            <button
              onClick={dismiss}
              data-testid="offer-dismiss-btn"
              className="mt-3 w-full rounded-full border border-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors hover:border-signal/50 hover:text-white"
            >
              Dismiss Offer
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
